import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GamificationService from '../services/GamificationService';

const GamificationHeader = () => {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [streak, setStreak] = useState(null);
  const [starAnim, setStarAnim] = useState(null);

  const loadData = useCallback(async () => {
    if (!api) return;
    const svc = GamificationService.getInstance();
    svc.setApi(api);
    const [summaryData, streakData] = await Promise.all([
      svc.getSummary(),
      svc.getStreak(),
    ]);
    setSummary(summaryData);
    setStreak(streakData);
  }, [api]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    const handler = (e) => {
      const { amount, newBalance } = e.detail;
      setStarAnim({ amount });
      setSummary(prev => prev ? { ...prev, stars: newBalance } : prev);
      setTimeout(() => setStarAnim(null), 2000);
    };
    window.addEventListener('stars-earned', handler);
    return () => window.removeEventListener('stars-earned', handler);
  }, []);

  if (!summary || !summary.has_asked_first_question) return null;

  const names = streak?.children_names || [];
  const displayName = names.length > 1 ? names.join(' et ') : (names[0] || '');
  const streakCount = streak?.streak_count || 0;

  return (
    <>
      {/* Bandeau streak — dans le flux, scrolle avec la page */}
      <div
        className="w-full flex items-center justify-between px-4 py-2 relative z-20"
        style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        data-testid="gamification-header"
      >
        <div className="flex items-center gap-1.5" data-testid="streak-banner">
          {streakCount > 0 ? (
            <>
              <span className="text-lg leading-none">&#128293;</span>
              <span className="text-sm font-bold text-orange-700">
                {streakCount} jour{streakCount > 1 ? 's' : ''} de suite
              </span>
              {displayName && (
                <span className="text-sm text-orange-600/80">
                  avec {displayName}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">Posez une question pour lancer votre serie !</span>
          )}
        </div>
        {/* Spacer pour que le streak ne passe pas sous la bulle étoiles fixe */}
        <div className="w-16" />
      </div>

      {/* Bulle étoiles — fixée en haut à droite, toujours visible */}
      <div
        className="fixed top-2 right-3 z-50 flex items-center gap-1 bg-amber-50 rounded-full px-3 py-1 border border-amber-200 shadow-sm"
        data-testid="star-counter"
      >
        <span className="text-amber-500 text-sm">&#11088;</span>
        <span className="text-sm font-bold text-amber-700" data-testid="star-balance">
          {summary.stars}
        </span>
        {starAnim && (
          <span
            className="absolute -top-5 left-1/2 -translate-x-1/2 text-amber-500 font-bold text-sm pointer-events-none"
            style={{ animation: 'starFloat 1.5s ease-out forwards' }}
          >
            +{starAnim.amount}
          </span>
        )}
      </div>

      <style>{`
        @keyframes starFloat {
          0% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        }
      `}</style>
    </>
  );
};

export default GamificationHeader;
